/* =========================================================================
   COMPONENT KIT — React + inline styles, driven by the `T` token object.
   Ported from design/kit.jsx into an ES module. Every component reads
   semantic tokens; no raw hex in here.
   ========================================================================= */
import React, { useState, useEffect } from "react";
import { T } from "./tokens";

/* ---- tiny interaction hooks ---------------------------------------- */
export function useHover() {
  const [h, setH] = useState(false);
  return [h, { onMouseEnter: () => setH(true), onMouseLeave: () => setH(false) }];
}
export function useFocusRing() {
  const [f, setF] = useState(false);
  return [f, { onFocus: () => setF(true), onBlur: () => setF(false) }];
}

/* ---- Mono number (tabular financials) ------------------------------ */
export function Num({ children, color, weight = T.weight.medium, size = T.size.sm, style }) {
  return (
    <span style={{
      fontFamily: T.font.mono, fontSize: size, fontWeight: weight,
      fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em",
      color: color || T.color.text, ...style,
    }}>{children}</span>
  );
}

/* ---- Caps label ---------------------------------------------------- */
export function Caps({ children, color, style }) {
  return (
    <span style={{
      fontFamily: T.font.sans, fontSize: T.size.micro, fontWeight: T.weight.semibold,
      letterSpacing: T.tracking.caps, textTransform: "uppercase",
      color: color || T.color.textMuted, ...style,
    }}>{children}</span>
  );
}

/* ---- Button: primary | secondary | ghost | danger ------------------ */
export function Button({ variant = "secondary", size = "md", children, leftIcon, onClick, disabled, style, active, title }) {
  const [hov, hp] = useHover();
  const [foc, fp] = useFocusRing();
  const pads = { sm: "5px 10px", md: "7px 14px", lg: "9px 18px" };
  const fs = { sm: T.size.sm, md: T.size.base, lg: T.size.md }[size];

  const base = {
    display: "inline-flex", alignItems: "center", gap: 7, cursor: disabled ? "not-allowed" : "pointer",
    fontFamily: T.font.sans, fontSize: fs, fontWeight: T.weight.semibold, lineHeight: 1,
    padding: pads[size], borderRadius: T.radius.md, border: "1px solid transparent",
    transition: `all ${T.motion.fast}`, whiteSpace: "nowrap", userSelect: "none",
    opacity: disabled ? 0.45 : 1,
    boxShadow: foc ? T.shadow.ring : undefined,
  };
  const variants = {
    primary: { background: hov ? T.color.accentHover : T.color.accent, color: T.color.accentText, borderColor: "transparent", boxShadow: foc ? T.shadow.ring : T.shadow.e1 },
    secondary: { background: hov ? T.color.surfaceHover : T.color.surface, color: T.color.text, borderColor: T.color.borderStrong, boxShadow: foc ? T.shadow.ring : T.shadow.e1 },
    ghost: { background: hov ? T.color.surfaceHover : "transparent", color: hov ? T.color.text : T.color.textSecondary, borderColor: "transparent" },
    danger: { background: hov ? "rgba(239,107,125,0.16)" : T.color.excludeSoft, color: T.color.exclude, borderColor: T.color.excludeBorder },
  };
  const activeMix = active ? { background: T.color.accentSoft, color: T.color.accent } : null;
  return (
    <button onClick={disabled ? undefined : onClick} disabled={disabled} title={title}
      {...hp} {...fp} style={{ ...base, ...variants[variant], ...activeMix, ...style }}>
      {leftIcon && <span style={{ fontSize: fs + 1, display: "inline-flex" }}>{leftIcon}</span>}
      {children}
    </button>
  );
}

/* ---- Card ---------------------------------------------------------- */
export function Card({ children, raised, pad = T.space[6], style, hover, onClick }) {
  const [hov, hp] = useHover();
  return (
    <div {...(hover ? hp : {})} onClick={onClick} style={{
      background: raised ? T.color.surfaceRaised : T.color.surface,
      border: `1px solid ${hov && hover ? T.color.borderStrong : T.color.border}`,
      borderRadius: T.radius.lg, padding: pad,
      boxShadow: raised ? T.shadow.e2 : T.shadow.e1,
      transition: `border-color ${T.motion.base}, background ${T.motion.base}`,
      cursor: onClick ? "pointer" : undefined, ...style,
    }}>{children}</div>
  );
}

/* ---- Badge / Pill -------------------------------------------------- */
export const STATUS = {
  include: { fg: () => T.color.include, soft: () => T.color.includeSoft, bd: () => T.color.includeBorder, label: "Include", dot: "✓" },
  exclude: { fg: () => T.color.exclude, soft: () => T.color.excludeSoft, bd: () => T.color.excludeBorder, label: "Exclude", dot: "✕" },
  review:  { fg: () => T.color.review,  soft: () => T.color.reviewSoft,  bd: () => T.color.reviewBorder,  label: "Human Review", dot: "⚠" },
};
export function Pill({ status, children, solid, size = "md", icon, style }) {
  const s = STATUS[status];
  const fg = s ? s.fg() : T.color.textSecondary;
  const soft = s ? s.soft() : T.color.surfaceHover;
  const bd = s ? s.bd() : T.color.border;
  const pad = size === "sm" ? "2px 7px" : "3px 9px";
  const fs = size === "sm" ? T.size.micro : T.size.caption;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, padding: pad,
      borderRadius: T.radius.pill, fontFamily: T.font.sans, fontSize: fs,
      fontWeight: T.weight.semibold, lineHeight: 1.1, whiteSpace: "nowrap",
      letterSpacing: T.tracking.wide,
      background: solid ? fg : soft, color: solid ? T.color.accentText : fg,
      border: `1px solid ${solid ? "transparent" : bd}`, ...style,
    }}>
      {(icon || (s && s.dot)) && <span style={{ fontSize: fs - 0.5 }}>{icon || s.dot}</span>}
      {children || (s && s.label)}
    </span>
  );
}

/* neutral tag (sources, flags) */
export function Tag({ children, color, soft, style }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "2px 7px", borderRadius: T.radius.sm,
      fontFamily: T.font.sans, fontSize: T.size.micro, fontWeight: T.weight.semibold,
      letterSpacing: T.tracking.caps, textTransform: "uppercase",
      color: color || T.color.textMuted, background: soft || T.color.bgInset,
      border: `1px solid ${T.color.border}`, ...style,
    }}>{children}</span>
  );
}

/* ---- Tabs ---------------------------------------------------------- */
export function Tabs({ tabs, value, onChange }) {
  return (
    <div style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
      {tabs.map(t => (
        <Tab key={t.id} active={t.id === value} onClick={() => onChange(t.id)} label={t.label} count={t.count} badge={t.badge} badgeColor={t.badgeColor} />
      ))}
    </div>
  );
}
export function Tab({ active, onClick, label, count, badge, badgeColor }) {
  const [hov, hp] = useHover();
  return (
    <button onClick={onClick} {...hp} style={{
      display: "inline-flex", alignItems: "center", gap: 7, cursor: "pointer",
      padding: "6px 12px", borderRadius: T.radius.md,
      fontFamily: T.font.sans, fontSize: T.size.sm, fontWeight: T.weight.semibold,
      background: active ? T.color.surfaceActive : (hov ? T.color.surfaceHover : "transparent"),
      color: active ? T.color.text : T.color.textMuted,
      border: `1px solid ${active ? T.color.borderStrong : "transparent"}`,
      boxShadow: active ? T.shadow.e1 : "none",
      transition: `all ${T.motion.fast}`,
    }}>
      {label}
      {count != null && (
        <span style={{ fontFamily: T.font.mono, fontSize: T.size.caption, fontWeight: T.weight.semibold, color: active ? T.color.textSecondary : T.color.textFaint }}>{count}</span>
      )}
      {badge != null && (
        <span style={{ fontFamily: T.font.sans, fontSize: T.size.micro, fontWeight: T.weight.bold, padding: "1px 6px", borderRadius: T.radius.pill, letterSpacing: T.tracking.wide, color: badgeColor || T.color.review, background: T.color.reviewSoft }}>{badge}</span>
      )}
    </button>
  );
}

/* ---- Text input ---------------------------------------------------- */
// [WPB1 2026-08-04] +onKeyDown/+autoFocus pass-through: the in-page prompts that replaced
// window.prompt() need Enter-to-submit / Escape-to-cancel and an auto-focused field. Additive —
// existing call sites pass neither and are unchanged.
export function Input({ value, onChange, placeholder, leftIcon, size = "md", style, type = "text", mono, onKeyDown, autoFocus }) {
  const [foc, fp] = useFocusRing();
  const pad = size === "sm" ? "6px 10px" : "8px 12px";
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8, width: "100%",
      background: T.color.bgInset, borderRadius: T.radius.md,
      border: `1px solid ${foc ? T.color.accent : T.color.borderStrong}`,
      boxShadow: foc ? T.shadow.ring : "none", padding: pad,
      transition: `border-color ${T.motion.fast}, box-shadow ${T.motion.fast}`, ...style,
    }}>
      {leftIcon && <span style={{ color: T.color.textMuted, display: "inline-flex", fontSize: 15 }}>{leftIcon}</span>}
      <input type={type} value={value} onChange={onChange} placeholder={placeholder} onKeyDown={onKeyDown} autoFocus={autoFocus} {...fp}
        style={{
          flex: 1, minWidth: 0, background: "transparent", border: "none", outline: "none",
          color: T.color.text, fontFamily: mono ? T.font.mono : T.font.sans,
          fontSize: size === "sm" ? T.size.sm : T.size.base, fontWeight: T.weight.regular,
        }} />
    </div>
  );
}

/* ---- Select (styled native) --------------------------------------- */
export function Select({ value, onChange, options, size = "md", style }) {
  const [foc, fp] = useFocusRing();
  const [hov, hp] = useHover();
  return (
    <div {...hp} style={{ position: "relative", display: "inline-flex", width: "100%" }}>
      <select value={value} onChange={onChange} {...fp} style={{
        appearance: "none", WebkitAppearance: "none", width: "100%",
        padding: size === "sm" ? "6px 30px 6px 10px" : "8px 32px 8px 12px",
        background: hov ? T.color.surfaceHover : T.color.surface,
        color: T.color.text, fontFamily: T.font.sans,
        fontSize: size === "sm" ? T.size.sm : T.size.base, fontWeight: T.weight.medium,
        border: `1px solid ${foc ? T.color.accent : T.color.borderStrong}`,
        borderRadius: T.radius.md, boxShadow: foc ? T.shadow.ring : "none",
        cursor: "pointer", outline: "none", transition: `all ${T.motion.fast}`,
      }}>
        {options.map(o => <option key={o.value ?? o} value={o.value ?? o} style={{ background: T.color.surfaceRaised }}>{o.label ?? o}</option>)}
      </select>
      <span style={{ position: "absolute", right: 11, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: T.color.textMuted, fontSize: 11 }}>▾</span>
    </div>
  );
}

/* ---- Modal --------------------------------------------------------- */
export function Modal({ open, onClose, children, width = 560, padded = true }) {
  useEffect(() => {
    if (!open) return;
    const h = e => e.key === "Escape" && onClose && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center",
      background: "rgba(5,6,8,0.66)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", padding: 24,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width, maxWidth: "94vw", maxHeight: "88vh", overflowY: "auto", overflowX: "hidden",
        background: T.color.surfaceRaised, border: `1px solid ${T.color.borderStrong}`,
        borderRadius: T.radius.xl, boxShadow: T.shadow.e3, padding: padded ? T.space[7] : 0,
      }}>{children}</div>
    </div>
  );
}

/* ---- Stat card ----------------------------------------------------- */
export function StatCard({ label, value, unit, delta, accent, icon, sub }) {
  const [hov, hp] = useHover();
  return (
    <div {...hp} style={{
      background: T.color.surface, border: `1px solid ${hov ? T.color.borderStrong : T.color.border}`,
      borderRadius: T.radius.lg, padding: `${T.space[5]}px ${T.space[6]}px`,
      boxShadow: T.shadow.e1, transition: `border-color ${T.motion.base}`,
      display: "flex", flexDirection: "column", gap: 10, minWidth: 0,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Caps>{label}</Caps>
        {icon && <span style={{ color: accent || T.color.textFaint, fontSize: 15 }}>{icon}</span>}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <span style={{ fontFamily: T.font.mono, fontSize: T.size.stat, fontWeight: T.weight.semibold, color: accent || T.color.text, letterSpacing: "-0.02em", lineHeight: 1 }}>{value}</span>
        {unit && <span style={{ fontFamily: T.font.sans, fontSize: T.size.sm, fontWeight: T.weight.medium, color: T.color.textMuted }}>{unit}</span>}
      </div>
      {(sub || delta != null) && (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {delta != null && <Num size={T.size.caption} color={delta >= 0 ? T.color.include : T.color.exclude}>{delta >= 0 ? "▲" : "▼"} {Math.abs(delta)}%</Num>}
          {sub && <span style={{ fontSize: T.size.caption, color: T.color.textMuted }}>{sub}</span>}
        </div>
      )}
    </div>
  );
}

/* ---- Section header ------------------------------------------------ */
export function SectionTitle({ children, right, sub }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: T.space[4] }}>
      <div>
        <div style={{ fontFamily: T.font.sans, fontSize: T.size.lg, fontWeight: T.weight.semibold, color: T.color.text, letterSpacing: T.tracking.tightNeg }}>{children}</div>
        {sub && <div style={{ fontSize: T.size.sm, color: T.color.textMuted, marginTop: 3 }}>{sub}</div>}
      </div>
      {right}
    </div>
  );
}
